import { toast } from 'react-toastify';
import { useAPI } from 'src/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import loadable from '@loadable/component';

const ErrorMsg = loadable(() => import('@com/utils/ErrorMsg'));
const Footer = loadable(() => import('@com/footer/Footer'));
const Header = loadable(() => import('@com/header/Header'));
const TopSection = loadable(() => import('@com/TopSection'));

interface FormInputs {
  password: string;
  confirmPassword: string;
}

function ResetPassword(): JSX.Element {
  /* Define state / hook */
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uuid = String(searchParams.get('u'));
  const oneTimeKey = String(searchParams.get('k'));
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormInputs>();
  const { t } = useTranslation();
  const { changePassword } = useAPI();

  /* Custom Data Object for render or other usages */
  const MIN_PASSWORD_LENGTH = 8;

  /* Render control function / logic */
  const onSubmit = useDebouncedCallback(
    async (data: FormInputs) => {
      await changePassword({
        uuid,
        oneTimeKey,
        password: data.password,
        confirmPassword: data.confirmPassword,
      });
      toast.success(t('toastMessage.newPasswordUpdated'));
      navigate('/');
    },
    500,
    { leading: true, trailing: false }
  );

  /* Data Change Listeners - useEffect/useMemo/useCallback */
  useEffect(() => {
    if (!uuid || !oneTimeKey) {
      toast.error(t('toastMessage.unexceptedError'));
      navigate('/no-result');
    }
  }, []);

  /* Styling */

  /* Render */
  return (
    <>
      <Header />
      <TopSection sectionName={t('signUpPage.changePassword')} />

      <section className="content-container tf-login tf-section">
        <div
          className="themesflat-container"
          style={{ minHeight: '40vh', marginBottom: '80px' }}
        >
          <div className="row">
            <div className="col-12">
              <div className="flat-form box-login-email">
                <div className="form-inner">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <input
                      aria-required
                      aria-label="password"
                      data-test="set-pwd-input"
                      placeholder={t('signUpPage.setYourPassword')}
                      type="password"
                      {...register('password', {
                        required: {
                          value: true,
                          message: t('errorMsg.register.passwordRequired'),
                        },
                        minLength: {
                          value: MIN_PASSWORD_LENGTH,
                          message: t('errorMsg.login.passwordMinLength', {
                            length: MIN_PASSWORD_LENGTH,
                          }),
                        },
                      })}
                    />
                    <ErrorMsg errors={errors} name="password" />
                    <input
                      aria-required
                      aria-label="confirmPassword"
                      data-test="confirm-set-pwd-input"
                      placeholder={t('signUpPage.confirmYourPassword')}
                      type="password"
                      {...register('confirmPassword', {
                        required: {
                          value: true,
                          message: t('errorMsg.register.passwordRequired'),
                        },
                        minLength: {
                          value: MIN_PASSWORD_LENGTH,
                          message: t('errorMsg.login.passwordMinLength', {
                            length: MIN_PASSWORD_LENGTH,
                          }),
                        },
                        validate: {
                          value: (value) =>
                            value === getValues('password') ||
                            t('errorMsg.register.passwordUnmatch'),
                        },
                      })}
                    />
                    <ErrorMsg errors={errors} name="confirmPassword" />
                    <button
                      className="mt-5"
                      data-test="confirm-change-pwd-btn"
                      type="submit"
                    >
                      {t('signUpPage.change')}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default ResetPassword;
